import {
  Avatar,
  Button,
  Drawer,
  Form,
  getValueFromEvent,
  Grid,
  Icons,
  Input,
  InputNumber,
  notification,
  Select,
  Space,
  Upload,
} from '@pankod/refine-antd';
import { file2Base64 } from '@pankod/refine-core';
import defaultAvatar from 'assets/images/default-avatar.jpg';
import { EPaymentType } from 'enums';
import { IPaymentLog } from 'interfaces';
import { useEffect, useState } from 'react';
import { getAllCompanyConfig } from 'services/config';
import { getPaymentMethods, getPaymentLogs, updatePaymentLog } from 'services/payment';
import {
  beforeReceiptUploadValidation,
  isLessThanSize,
  isPurchaseReceiptFile,
  MAX_UPLOAD_FILE_SIZE_MB,
} from 'utilities/file';
import styles from './style.module.css';

interface IPaymentLogState extends IPaymentLog {
  currentPendingAmount: number;
}

type EditPaymentLogProps = {
  resource: 'sales' | 'purchase';
  resourceId: string;
  paymentLog: IPaymentLog;
  paymentBy: 'client' | 'supplier';
  paymentById: string;
  totalPayableAmount: number;
  totalPendingAmount: number;
  setPaymentLogs: React.Dispatch<React.SetStateAction<IPaymentLogState[]>>;
};

interface IPaymentMethodOption {
  id: string;
  name: string;
}

const { Option } = Select;

export const EditPaymentLog: React.FC<EditPaymentLogProps> = (props) => {
  const [visible, setVisible] = useState<boolean>(false);
  const [paymentMethods, setPaymentMethods] = useState<IPaymentMethodOption[]>([]);
  const [companyPaymentMethods, setCompanyPaymentMethods] = useState<IPaymentMethodOption[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isPaymentMethodDisable, setIsPaymentMethodDisable] = useState<boolean>(
    props.paymentLog.paymentType === EPaymentType.Cash
  );

  const [form] = Form.useForm();
  const breakpoint = Grid.useBreakpoint();
  const salesPaymentTypes = [
    EPaymentType.Cash,
    EPaymentType.Cheque,
    EPaymentType.Card,
    EPaymentType.Bkash,
    EPaymentType.Nagad,
  ];
  const purchasePaymentTypes = [EPaymentType.Cash, EPaymentType.Cheque];
  const paymentTypes = props.resource === 'sales' ? salesPaymentTypes : purchasePaymentTypes;
  const paymentType = Form.useWatch('paymentType', form);

  useEffect(() => {
    if (!visible) return;

    (async () => {
      try {
        const data = await getAllCompanyConfig();
        const companyId = data[0].id;
        const [paymentMethodsResponse, companyPaymentMethodsResponse] = await Promise.all([
          getPaymentMethods(props.paymentBy, props.paymentById),
          getPaymentMethods('company', companyId),
        ]);

        const extendedPaymentMethods = paymentMethodsResponse.map((paymentMethod) => ({
          id: paymentMethod.id,
          name: paymentMethod.bankName,
        }));

        const extendedCompanyPaymentMethods = companyPaymentMethodsResponse.map(
          (paymentMethod) => ({
            id: paymentMethod.id,
            name: paymentMethod.bankName,
          })
        );

        setPaymentMethods(extendedPaymentMethods);
        setCompanyPaymentMethods(extendedCompanyPaymentMethods);
      } catch (err) {
        if (err instanceof Error) {
          notification.error({
            message: 'Failed',
            description: err.message,
          });
        }
        setPaymentMethods([]);
        setCompanyPaymentMethods([]);
      }
    })();
  }, [props.paymentBy, props.paymentById, visible]);

  const showDrawer = () => {
    setVisible(true);
  };

  const closeDrawer = () => {
    form.resetFields();
    setVisible(false);
  };

  const getOptions = (items: IPaymentMethodOption[]) => {
    return items.map((item) => (
      <Option key={item.id} value={item.id}>
        {item.name}
      </Option>
    ));
  };

  const getOptionsCompany = (items: IPaymentMethodOption[]) => {
    return items.map((item) => (
      <Option key={item.id} value={item.id}>
        {item.name}
      </Option>
    ));
  };

  const onPaymentTypeSelect = (value: EPaymentType) => {
    form.resetFields(['paymentMethod', 'companyPaymentMethod']);
    if (value === EPaymentType.Cheque) {
      setIsPaymentMethodDisable(false);
    } else {
      setIsPaymentMethodDisable(true);
    }
  };

  const getAttachments = () => {
    return props.paymentLog.attachments.map((item, index) => (
      <Space key={index} style={{ marginRight: '0.5rem' }}>
        <Avatar
          size={70}
          style={{ objectFit: 'cover' }}
          src={item.attachment.url || defaultAvatar}
          alt={item.attachment.name}
        />
      </Space>
    ));
  };

  const onFinish = async (values: any) => {
    setIsLoading(true);
    const {
      paymentType,
      paymentMethod,
      paymentId,
      paymentDetails,
      currentPaidAmount,
      attachments,
      companyPaymentMethod,
      phoneNumber,
      transactionId,
      cardNumber,
      approvalCode,
    } = values;
    let base64Files = [];

    if (
      currentPaidAmount >
      Number.parseFloat(props.totalPendingAmount.toFixed(2)) +
        Number.parseFloat(props.paymentLog.currentPaidAmount.toFixed(2))
    ) {
      form.setFields([
        {
          name: 'currentPaidAmount',
          errors: [
            `Payment exceed total pending amount (${
              props.totalPendingAmount + props.paymentLog.currentPaidAmount
            })`,
          ],
        },
      ]);
      setIsLoading(false);
      return;
    }

    if (attachments && attachments.length) {
      for (const file of attachments) {
        const format = file.type || '';
        const size = file.size || 0;
        if (isPurchaseReceiptFile(format) && isLessThanSize(size, MAX_UPLOAD_FILE_SIZE_MB)) {
          if (file.originFileObj) {
            const base64String = await file2Base64(file);
            const { originFileObj, ...rest } = file;
            base64Files.push({ ...rest, base64String });
          } else {
            base64Files.push(file);
          }
        }
      }
    } else {
      base64Files = props.paymentLog.attachments.map((item) => item.attachment);
    }

    try {
      const data: any = {
        paymentType,
        paymentMethod,
        paymentDetails,
        currentPaidAmount,
        paymentDate: props.paymentLog.paymentDate,
        attachments: base64Files,
        companyBank: companyPaymentMethod,
        paymentMeta: null,
      };

      // Conditionally build and assign paymentMeta
      if (props.resource === 'sales' && paymentType === EPaymentType.Card) {
        data.paymentMeta = {
          cardNo: cardNumber,
          approvalCode: approvalCode,
        };
      }

      if (
        props.resource === 'sales' &&
        (paymentType === EPaymentType.Bkash || paymentType === EPaymentType.Nagad)
      ) {
        data.paymentMeta = {
          phoneNo: phoneNumber,
          transactionId: transactionId,
        };
      }

      data[`${props.resource}Copy`] = values[props.resource];

      await updatePaymentLog(props.resource === 'sales' ? 'sale' : props.resource, paymentId, data);
      let currentPendingAmount = props.totalPayableAmount;
      const paymentLogs = await getPaymentLogs(props.resource, props.resourceId);
      const extendedPaymentLogs = paymentLogs.map((paymentLog) => {
        currentPendingAmount -= paymentLog.currentPaidAmount;
        return { ...paymentLog, currentPendingAmount };
      });
      props.setPaymentLogs(extendedPaymentLogs);

      notification.success({
        message: 'Success',
        description: `Successfully updated payment`,
      });
    } catch (err: any) {
      notification.error({
        message: 'Failed',
        description: err.message,
      });
    }

    closeDrawer();
    setIsLoading(false);
  };

  const paymentMethodId =
    props.paymentLog.paymentType === EPaymentType.Cash
      ? props.paymentLog.paymentMethod
      : props.paymentLog.paymentMethod?.id;

  const clientOrSupplierPaymentMethodTitle =
    props.paymentBy.toLowerCase() === 'client' ? 'Paying Bank' : 'Receiving Bank';
  const companyPaymentMethodTitle =
    props.paymentBy.toLowerCase() === 'client' ? 'Receiving Bank' : 'Paying Bank';

  return (
    <>
      {!visible && <Button size="small" onClick={showDrawer} icon={<Icons.EditOutlined />} />}
      <Drawer
        title="Edit payment"
        placement="right"
        width={breakpoint.sm ? '450px' : '100%'}
        visible={visible}
        onClose={closeDrawer}
      >
        <Form
          layout="vertical"
          initialValues={{
            [props.resource]: props.resourceId,
            paymentId: props.paymentLog.id,
            paymentType: props.paymentLog.paymentType,
            paymentMethod: paymentMethodId,
            companyPaymentMethod: props.paymentLog.companyBank?.id,
            paymentDetails: props.paymentLog.paymentDetails,
            currentPaidAmount: props.paymentLog.currentPaidAmount,
          }}
          form={form}
          onFinish={onFinish}
        >
          <Space direction="vertical" className={styles['file-upload']}>
            <Form.Item
              name="attachments"
              valuePropName="fileList"
              getValueFromEvent={getValueFromEvent}
              rules={[
                {
                  required: false,
                  message: 'Attachment is required',
                },
              ]}
            >
              <Upload.Dragger
                listType="picture"
                accept="image/jpeg, image/png, image/heic, image/heif, .heic, .heif, application/pdf"
                multiple
                maxCount={2}
                beforeUpload={beforeReceiptUploadValidation}
              >
                {getAttachments()}
                <p className="ant-upload-text">Drag & Drop</p>
                <p className="ant-upload-text">Image or PDF files in this area</p>
                <p className="ant-upload-hint">
                  {`Files must be less than ${MAX_UPLOAD_FILE_SIZE_MB}MB and maximum file count is 2`}
                </p>
              </Upload.Dragger>
            </Form.Item>
          </Space>

          <Form.Item
            label={`${props.resource} ID`}
            name={props.resource}
            rules={[
              {
                required: true,
              },
            ]}
            style={{ display: 'none' }}
          >
            <Input disabled />
          </Form.Item>

          <Form.Item
            label={`Payment ID`}
            name="paymentId"
            rules={[
              {
                required: true,
              },
            ]}
            style={{ display: 'none' }}
          >
            <Input disabled />
          </Form.Item>

          <Form.Item
            label="Payment Type"
            name="paymentType"
            rules={[{ required: true, message: 'Please select a type' }]}
          >
            <Select
              showSearch
              placeholder="Select a type"
              onSelect={onPaymentTypeSelect}
              filterOption={(input, option: any) =>
                option!.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {paymentTypes.map((item) => (
                <Option key={item} value={item}>
                  {item}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {/* Conditional fields for Bkash and Nagad */}
          {(paymentType === EPaymentType.Bkash || paymentType === EPaymentType.Nagad) && (
            <>
              <Form.Item
                label="Phone Number"
                name="phoneNumber"
                rules={[{ required: true, message: 'Please enter phone number' }]}
              >
                <Input prefix="XXX XXXX " maxLength={4} />
              </Form.Item>
              <Form.Item
                label="Transaction ID"
                name="transactionId"
                rules={[{ required: true, message: 'Please enter transaction ID' }]}
              >
                <Input placeholder="e.g. 456" />
              </Form.Item>
            </>
          )}

          {/* Conditional fields for Card */}
          {paymentType === EPaymentType.Card && (
            <>
              <Form.Item
                label="Card Number (4 digit)"
                name="cardNumber"
                rules={[{ required: true, message: 'Please enter card number' }]}
              >
                <Input prefix="XXXX XXXX " maxLength={4} />
              </Form.Item>
              <Form.Item
                label="Approval Code"
                name="approvalCode"
                rules={[{ required: true, message: 'Please enter approval code' }]}
              >
                <Input placeholder="e.g. 4567" />
              </Form.Item>
            </>
          )}

          {/* Conditional fields for Cheque */}
          {paymentType === EPaymentType.Cheque && (
            <>
              <Form.Item
                label={clientOrSupplierPaymentMethodTitle}
                name="paymentMethod"
                rules={[{ required: false, message: 'Please select a method' }]}
              >
                <Select
                  showSearch
                  placeholder="Select a method"
                  filterOption={(input, option: any) =>
                    option!.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  disabled={isPaymentMethodDisable}
                >
                  {getOptions(paymentMethods)}
                </Select>
              </Form.Item>
              <Form.Item
                label={companyPaymentMethodTitle}
                name="companyPaymentMethod"
                rules={[
                  {
                    required: true && !isPaymentMethodDisable,
                    message: 'Please select a method',
                  },
                ]}
              >
                <Select
                  showSearch
                  placeholder="Select a method"
                  filterOption={(input, option: any) =>
                    option!.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  disabled={isPaymentMethodDisable}
                >
                  {getOptionsCompany(companyPaymentMethods)}
                </Select>
              </Form.Item>
            </>
          )}

          <Form.Item label="Payment Details" name="paymentDetails">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item
            label="Payment Amount"
            name="currentPaidAmount"
            rules={[{ required: true, type: 'number' }]}
          >
            <InputNumber min={1} style={{ width: '100%' }} precision={2} />
          </Form.Item>

          <Form.Item wrapperCol={{ span: 4, offset: 20 }}>
            <Button type="primary" htmlType="submit" loading={isLoading}>
              Save
            </Button>
          </Form.Item>
        </Form>
      </Drawer>
    </>
  );
};
